import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_LOCALE,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  SITE_BRAND,
  SITE_NAME,
  TITLE_TEMPLATE,
  toAbsoluteUrl,
} from "./siteSeo";

interface SeoHeadProps {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  keywords?: string;
  noindex?: boolean;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

export default function SeoHead({
  title,
  description,
  path,
  image,
  keywords,
  noindex = false,
  type = "website",
  jsonLd,
}: SeoHeadProps) {
  const { pathname } = useLocation();
  const effectivePath = path ?? pathname;
  const canonical = toAbsoluteUrl(effectivePath);
  const fullTitle = title ? TITLE_TEMPLATE.replace("%s", title) : DEFAULT_TITLE;
  const metaDescription = description ?? DEFAULT_DESCRIPTION;
  const metaKeywords = keywords ?? DEFAULT_KEYWORDS;
  const ogImage = image ? toAbsoluteUrl(image) : DEFAULT_OG_IMAGE;
  const robots = noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large";
  const googleVerification = import.meta.env.VITE_GOOGLE_SITE_VERIFICATION as string | undefined;

  const jsonLdPayload = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : null;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta name="author" content={SITE_NAME} />
      <meta name="application-name" content={SITE_NAME} />
      <meta name="apple-mobile-web-app-title" content={SITE_BRAND} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:locale" content={DEFAULT_LOCALE} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={`${SITE_NAME} logosu`} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      <meta name="geo.region" content="TR-34" />
      <meta name="geo.placename" content="Pendik, İstanbul" />

      {googleVerification ? (
        <meta name="google-site-verification" content={googleVerification} />
      ) : null}

      {jsonLdPayload ? (
        <script type="application/ld+json">{JSON.stringify(jsonLdPayload)}</script>
      ) : null}
    </Helmet>
  );
}
